import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DirectConnectProvisionMachineInput,
  useProvisionDirectConnectMachineMutation,
} from '../../api/graphql/generated';
import { logger } from '../../util/logger';
import { pollDirectConnectHealth } from '../api/direct-connect-health-api';
import {
  DirectConnectTask,
  DirectConnectTaskType,
} from '../model/direct-connect.model';
import { DirectConnectCacheConnectedMachine } from './direct-connect-cache-machine-gate';
import { DirectConnectGateSource } from './direct-connect-gate-keeper';
import { useDirectConnectHealthGateContext } from './direct-connect-health-gate';
import {
  DirectConnectTaskGate,
  DirectConnectTaskNotFoundResponse,
} from './direct-connect-task-gate';

interface DirectConnectProvisioningGateProps {
  children: ReactNode;
}

const DirectConnectProvisioningGate = (
  props: DirectConnectProvisioningGateProps
) => {
  const { children } = props;

  const { i18n } = useTranslation();

  const { healthUrl, forceHealthCheck, healthStatus } =
    useDirectConnectHealthGateContext();
  const [provisionDirectConnectMachine] =
    useProvisionDirectConnectMachineMutation();

  const isProvisioned = healthStatus?.isRegistered;

  const executeTask = useCallback(
    async (task: DirectConnectTask<DirectConnectProvisionMachineInput>) => {
      const { serialNumber, payload } = task;

      logger.info('provisioning direct connect machine', {
        serialNumber,
      });

      const provisionResult = await provisionDirectConnectMachine({
        variables: {
          input: payload,
        },
      });

      if (!provisionResult.data?.provisionDirectConnectMachine.ok) {
        return {
          success: false,
          message: i18n.t('common:direct_connect_provisioning_general_error'),
        };
      }

      // begin polling for cloud connector health since provisioning
      // will force a restart

      const result = await pollDirectConnectHealth(healthUrl, {
        interval: 1_000,
        timeout: 30_000,
      });

      if (result === undefined) {
        return {
          success: false,
          message: i18n.t('common:direct_connect_provisioning_general_error'),
        };
      }

      logger.info('direct connect machine provisioned', {
        serialNumber,
      });

      // force health gate to check health again so data will propagate down
      // to all children
      forceHealthCheck();

      return {
        success: true,
      };
    },
    [i18n, provisionDirectConnectMachine, healthUrl, forceHealthCheck]
  );

  const onTaskNotFound =
    useCallback(async (): Promise<DirectConnectTaskNotFoundResponse> => {
      if (!isProvisioned) {
        return {
          success: false,
          message: i18n.t(
            'common:direct_connect_provisioning_request_not_found'
          ),
        };
      } else {
        return {
          success: true,
        };
      }
    }, [i18n, isProvisioned]);

  return (
    <DirectConnectTaskGate
      source={DirectConnectGateSource.PROVISION_GATE}
      taskType={DirectConnectTaskType.PROVISION}
      loaderMessage={i18n.t('common:direct_connect_provisioning')}
      defaultErrorMessage={i18n.t(
        'common:direct_connect_provisioning_request_not_found'
      )}
      executeTask={executeTask}
      onTaskNotFound={onTaskNotFound}
      skip={isProvisioned}
    >
      <DirectConnectCacheConnectedMachine healthStatus={healthStatus}>
        {children}
      </DirectConnectCacheConnectedMachine>
    </DirectConnectTaskGate>
  );
};

export { DirectConnectProvisioningGate };
